import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import GridHelper from "../helpers/gridHelper";
import FilterBar from "../common/FilterBar";
import { auditLogActionTypes, authenticationMethods, resourceTypes } from "../../constants/auditLog.constants";

// Matches the backend's actual filter semantics (AuditLogRepository.SelectAsync):
// email uses Contains(); ipAddress/affectedResourceId use exact equality; actionType is
// multi-value (comma-separated, ANY match — mirrors RunbookExecutionHistory's Status filter);
// isSuccessful/affectedResourceType/authenticationMethod are single-value equality against a
// small fixed set, hence FilterBar's 'select' type rather than 'multiselect'.
const ACTION_TYPE_OPTIONS = auditLogActionTypes.map((a) => ({ value: a.value, label: a.label }));
const RESOURCE_TYPE_OPTIONS = resourceTypes.map((r) => ({ value: r.value, label: r.label }));
const AUTH_METHOD_OPTIONS = authenticationMethods.map((m) => ({ value: m.value, label: m.label }));

function AuditLogGridFilter(props) {
  const location = useLocation();
  const navigate = useNavigate();

  const filterDefs = useMemo(() => {
    const defs = [
      {
        key: "email",
        label: "User Email",
        type: "text",
        operators: [{ value: "contains", label: "Contains" }],
      },
      {
        key: "actionType",
        label: "Action Type",
        type: "multiselect",
        operators: [{ value: "in", label: "Is one of" }],
        options: ACTION_TYPE_OPTIONS,
      },
      {
        key: "isSuccessful",
        label: "Status",
        type: "select",
        operators: [{ value: "equals", label: "Equals" }],
        options: [
          { value: "true", label: "Success" },
          { value: "false", label: "Failed" },
        ],
      },
      {
        key: "affectedResourceType",
        label: "Resource Type",
        type: "select",
        operators: [{ value: "equals", label: "Equals" }],
        options: RESOURCE_TYPE_OPTIONS,
      },
      {
        key: "affectedResourceId",
        label: "Resource ID",
        type: "text",
        operators: [{ value: "equals", label: "Equals" }],
      },
      {
        key: "authenticationMethod",
        label: "Authentication Method",
        type: "select",
        operators: [{ value: "equals", label: "Equals" }],
        options: AUTH_METHOD_OPTIONS,
      },
      {
        key: "ipAddress",
        label: "IP Address",
        type: "text",
        operators: [{ value: "equals", label: "Equals" }],
      },
      {
        key: "createdOn",
        label: "Date",
        type: "dateRange",
        fromKey: "startDate",
        toKey: "endDate",
        operators: [
          { value: "between", label: "Is between" },
          { value: "after", label: "Is after" },
          { value: "before", label: "Is before" },
        ],
      },
    ];

    // Super-user-only field, matching the old panel's visibility rule.
    if (props.user.isSuperUser) {
      defs.push({
        key: "clientId",
        label: "Client ID",
        type: "text",
        operators: [{ value: "equals", label: "Equals" }],
      });
    }

    return defs;
  }, [props.user.isSuperUser]);

  const applyFilter = (nextFilter) =>
    props.onSearch ? props.onSearch(nextFilter) : GridHelper.handleSearch(nextFilter, location, navigate);

  return <FilterBar filterDefs={filterDefs} value={props.filter} onChange={applyFilter} />;
}

export default AuditLogGridFilter;
